import { Node, Extension, mergeAttributes } from '@tiptap/core';
import {
	SCRIPT_ELEMENTS,
	SCRIPT_LYRIC,
	enterAction,
	isScriptElement,
	nextElement,
	prevElement,
} from '../../../services/scriptFormat';
import { ScriptCharacterPluginKey, hasCharacterMatches } from './ScriptCharacterSuggestion';

/* Docs — the script doc type's six elements, plus the keymap that makes them
   worth having (TipTap wiring only).
 *
 * The element model — the Tab cycle, the Enter advance/escape maps, character
 * extraction — lives in services/scriptFormat.js as pure functions, so it is
 * unit-tested. This file holds only what needs TipTap: the node schema and the
 * keyboard bindings.
 *
 * SEMANTIC NODES, NOT STYLED PARAGRAPHS. A centred paragraph cannot tell you it
 * is a character cue: it can't drive the keymap, can't be autocompleted
 * against, and stops meaning anything the moment someone restyles it. Format
 * measurements live in ScriptElements.css.
 */

/* Every element is the same shape — a textblock of inline content — and differs
   only in how it renders. `defining: true` keeps the type through a plain split
   (Enter mid-speech leaves both halves dialogue) and through paste. */
const makeElement = ({ name, tag, className }) =>
	Node.create({
		name,
		group: 'block',
		content: 'inline*',
		defining: true,

		parseHTML() {
			return [{ tag: `${tag}[data-script="${name}"]` }];
		},

		renderHTML({ HTMLAttributes }) {
			return [
				tag,
				mergeAttributes(HTMLAttributes, { 'data-script': name, class: className }),
				0,
			];
		},
	});

export const ScriptNodes = SCRIPT_ELEMENTS.map((el) =>
	makeElement({
		name: el.name,
		tag: 'p',
		className: `cyv-script__${el.name.replace('script', '').toLowerCase()}`,
	})
);

/* ---- Keymap ------------------------------------------------------------- */

/** Type names of every ancestor of the cursor, innermost first. */
function ancestorNames($from) {
	const names = [];
	for (let d = $from.depth; d > 0; d -= 1) names.push($from.node(d).type.name);
	return names;
}

/* Contexts where Tab already means something the writer needs more.
   Bailing explicitly rather than relying on extension ordering: table cell
   navigation and list indentation must keep working inside a script doc, and a
   silent hijack of Tab in a table would be maddening to diagnose. */
const TAB_BLOCKED_ANCESTORS = ['table', 'tableCell', 'tableHeader', 'listItem', 'taskItem'];

/* Blocks Tab must not retype. The H1 is the document title — turning it into a
   character cue because the cursor happened to be there would be destructive. */
const TAB_BLOCKED_NODES = ['heading', 'codeBlock'];

function canRetype(editor) {
	const { $from, empty } = editor.state.selection;
	if (!empty) return false;
	const ancestors = ancestorNames($from);
	if (ancestors.some((n) => TAB_BLOCKED_ANCESTORS.includes(n))) return false;
	if (TAB_BLOCKED_NODES.includes($from.parent.type.name)) return false;
	return true;
}

const ScriptKeymap = Extension.create({
	name: 'scriptKeymap',

	addKeyboardShortcuts() {
		/* Tab cycles the element type; Shift-Tab reverses. From a paragraph, both
		   enter script mode at Character — Tab is how you start writing a scene,
		   so refusing there would make the feature undiscoverable from a blank
		   line, which is exactly where a writer starts. */
		const cycle = (step) => ({ editor }) => {
			if (!canRetype(editor)) return false;
			const current = editor.state.selection.$from.parent.type.name;
			const target = step(current);
			if (!target || target === current) return false;
			return editor.chain().focus().setNode(target).run();
		};

		return {
			Tab: cycle(nextElement),
			'Shift-Tab': cycle(prevElement),

			Enter: ({ editor }) => {
				const { $from, empty } = editor.state.selection;
				if (!empty) return false;

				const current = $from.parent.type.name;
				if (!isScriptElement(current)) return false;

				/* Is the block before this one a blank lyric? That is what tells a
				   stanza break apart from the end of the song. */
				const index = $from.index($from.depth - 1);
				const parent = $from.node($from.depth - 1);
				const before = index > 0 ? parent.child(index - 1) : null;
				const prevIsBlankLyric =
					!!before && before.type.name === SCRIPT_LYRIC && before.content.size === 0;

				/* The character picker owns Enter while it is OFFERING something —
				   otherwise choosing a name would instead advance the element and
				   leave the cue blank. Read through the plugin key rather than
				   trusting extension order, so this holds however the extensions
				   sort themselves.

				   The match check is the load-bearing half: the picker is "active"
				   on every cue line, including while you type a brand-new name that
				   matches nobody. Yielding on `active` alone would mean Enter did
				   nothing at all the first time each character is introduced. */
				const picker = ScriptCharacterPluginKey.getState(editor.state);
				if (picker?.active && hasCharacterMatches(editor, picker.query)) return false;

				const { action, target } = enterAction({
					type: current,
					isEmpty: $from.parent.content.size === 0,
					atEnd: $from.parentOffset === $from.parent.content.size,
					prevIsBlankLyric,
				});

				if (action === 'default' || !target) return false;
				// Convert this empty block in place — no new block is created.
				if (action === 'escape') return editor.chain().focus().setNode(target).run();
				// 'advance' and 'stanza' both open a new block; they differ only in
				// what it becomes, which enterAction has already decided.
				return editor.chain().focus().splitBlock().setNode(target).run();
			},
		};
	},
});

export default ScriptKeymap;
